<template>
  <article>
    <activity-stage :customRootContainerClass="`${keyboardEnabled ? '' : 'full-height'}`">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <div v-if="!winner && currentRound.steps" class="grid">
          <div id="left-side">
            <span id="left-tube" :class="[onTablet ? 'pulled' : '']" v-html="tube"></span>
            <div
              v-for="(word, index) in side.left"
              :key="`word-${index}`"
              :class="{ container: true, isMobile: onTablet }"
              :aria-label="
                `${word.prefix ? `prefix ${word.prefix}, ` : ''}missing vowels${
                  word.suffix ? `, suffix ${word.suffix}` : ''
                }`
              "
              role="region"
            >
              <div v-text="word.prefix"></div>
              <div class="match" :id="`leftV${word.id}`"></div>
              <div v-text="word.suffix"></div>
            </div>
          </div>
          <div class="trampoline">
            <text-display-list element="div" v-show="input" id="input">
              <blinking-cursor-text
                :text="currentStep.vowels"
                :text-step="progress.stepIndex"
                :class="'step step-' + progress.stepIndex"
              ></blinking-cursor-text>
            </text-display-list>
            <figure id="trampoline-figure" v-html="trampoline"></figure>
          </div>
          <div id="right-side">
            <span id="right-tube" :class="[onTablet ? 'pulled' : '']" v-html="tube"></span>
            <div
              v-for="(word, index) in side.right"
              :key="`word-${index}`"
              :class="{ container: true, isMobile: onTablet }"
              :aria-label="
                `${word.prefix ? `prefix ${word.prefix}, ` : ''}missing vowels${
                  word.suffix ? `, suffix ${word.suffix}` : ''
                }`
              "
              role="region"
            >
              <div v-text="word.prefix"></div>
              <div class="match" :id="`rightV${word.id}`"></div>
              <div v-text="word.suffix"></div>
            </div>
          </div>
        </div>
        <div v-else id="winner">
          <figure>
            <div v-html="medal" id="medal"></div>
            <figcaption>
              {{ winner }}
              <br />WINS
            </figcaption>
          </figure>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

import medal from 'assets/images/activities/vowel-combo-launch/bouncing-vowels-medal.svg';
import trampoline from 'assets/images/activities/vowel-combo-launch/bouncing-vowels-trampoline.svg';
import tube from 'assets/images/activities/vowel-combo-launch/bouncing-vowels-tube.svg';
import boing from 'assets/audio/sfx/activities/BouncingVowels/boing.mp3';
import cork from 'assets/audio/sfx/activities/BouncingVowels/cork.mp3';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },
  data() {
    return {
      activityName: 'VowelComboLaunch',
      medal,
      trampoline,
      tube,
      boing,
      cork,
      score: {
        left: 0,
        right: 0
      },
      winner: '',
      input: true,
      sfx: null
    };
  },

  computed: {
    side() {
      const left = this.currentRound.steps.filter(({ isLeft }) => isLeft);
      const right = this.currentRound.steps.filter(({ isLeft }) => !isLeft);

      return { left, right } || 3;
    },

    onTablet() {
      return this.isMobile;
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      this.sfx = await this.loadSounds();
    },

    async loadSounds() {
      const self = this;
      const soundArray = [this.boing, this.cork];
      this.rounds.forEach(round => {
        soundArray.push(
          ...round.steps.map(
            ({ prefix, suffix, vowels }) =>
              `${self.spokenWordsPrefix}/${prefix || ''}${vowels}${suffix || ''}-mt.mp3`
          )
        );
      });

      this.queueAudio(soundArray);
      const cache = await this.downloadAudio();

      return {
        sndBoing() {
          return cache.get(self.boing).play();
        },
        sndCork() {
          return cache.get(self.cork).play();
        },
        playWord(word) {
          return cache.get(word).play();
        }
      };
    },

    getActivityState() {
      const {
        rounds,
        keyboard: { characters }
      } = this.activityData;

      return {
        rounds,
        instructions: {
          written: characters ? 'Hold the target. Type each letter' : 'Type each letter.',
          voice: `${this.voiceInstructionsPrefix}/bouncingvowels${
            characters ? '' : 'nth'
          }-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    getKeyboardConfig() {
      const { characters, keyboardType, roofs } = this.activityData.keyboard;
      const isTouchHold = !!characters;
      const showTargets = this.isMobile && !(this.ipadWithoutExternalKeyboard && isTouchHold);

      return {
        showTargets,
        keyboardType,
        roofs
      };
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentStep']['vowels'];
    },

    setBouncingVowelStep() {
      this.setCurrentStep({ stepCallback: this.stepCallback });
    },

    startActivity() {
      this.setupNewRound({
        stepCallback: this.stepCallback
      });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.scoreTo();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      this.hideKeyboard();

      timeline.delay(0.8).call(() => {
        this.winner = this.currentStep.vowels;
        if (this.isLastRound) {
          timeline.delay(2.5).call(this.completeActivity);
        } else {
          timeline
            .delay(2.5)
            .to('.activity-stage', 0.5, { opacity: 0 })
            .call(this.reset)
            .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
        }
      });
    },

    scoreTo() {
      const self = this;
      const timeline = new TimelineLite();
      const { isLeft, vowels, suffix, prefix, id } = this.currentStep;
      const side = isLeft ? 'left' : 'right';

      const input = document.getElementById('input');
      const figure = document.getElementsByTagName('figure')[0];
      const tubeSide = document.getElementById(`${side}-side`);
      const spanWinner = document.getElementById(`${side}V${id}`);

      const { left: inputX, top: inputY } = input.getBoundingClientRect();
      const { left: tubeX, top: tubeY } = tubeSide.getBoundingClientRect();
      const { left: spanX, top: spanY } = spanWinner.getBoundingClientRect();
      const { top: figureY } = figure.getBoundingClientRect();
      const thresold = (first, second) => first - second;
      const bezier = {
        values: [
          {
            left: 0,
            top: thresold(tubeY, inputY) + 135
          },
          {
            left: thresold(tubeX, inputX) / 3,
            top: thresold(tubeY, inputY) - (this.onTablet ? 0 : 25.5)
          },
          {
            left: thresold(tubeX, inputX) + (this.onTablet ? -18 : 18),
            top: thresold(tubeY, inputY) - (this.onTablet ? 0 : 55)
          }
        ]
      };

      this.score[side] += 1;

      timeline
        .to(input, 0.8, {
          delay: 0,
          top: thresold(figureY, inputY),
          onStart() {
            self.sndBell();
          }
        })
        .to(input, 0.5, {
          onStart() {
            self.sfx.sndBoing();
          },
          bezier
        })
        .to(input, 0.3, {
          delay: 0.15,
          onStart() {
            self.sfx.sndCork();
          },
          left: thresold(spanX, inputX) - (this.onTablet ? 13 : 0),
          top: thresold(spanY, inputY),
          onComplete() {
            const word = `${prefix || ''}${vowels}${suffix || ''}`;
            const wordMp3 = `${self.spokenWordsPrefix}/${prefix || ''}${vowels}${suffix ||
              ''}-mt.mp3`;

            self.sfx.playWord(wordMp3);

            self.setScreenReaderAlert(`You formed the word ${word}.`);
            spanWinner.parentNode.setAttribute(
              'aria-label',
              `${spanWinner.parentNode
                .getAttribute('aria-label')
                .replace('missing vowels', `vowels ${vowels}`)} forms word ${word}`
            );
            spanWinner.innerText = vowels;

            if (self.score[side] === 3) {
              self.input = false;
              self.handleRoundEnd();
              return;
            }
            self.setBouncingVowelStep();
          }
        })
        .set(input, { clearProps: 'all' });
    },

    reset() {
      this.input = true;
      this.winner = '';
      this.score.left = 0;
      this.score.right = 0;
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.showKeyboard();
      this.enableKeyboard();
    },

    setTrampolineLocation() {
      const scrollContainerEl = document.getElementById('scroll-container');
      const trampolineFigureEl = document.getElementById('trampoline-figure');

      if (scrollContainerEl && trampolineFigureEl) {
        trampolineFigureEl.style.top = `calc(${scrollContainerEl.scrollHeight}px - 124px)`;
      }
    },

    setScreenReaderKeyboardInstructions(instuctions) {
      const screenReaderAlertEl = document.getElementById('screenReaderAlert');
      if (screenReaderAlertEl) {
        screenReaderAlertEl.innerHTML = instuctions;
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.setTrampolineLocation);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setTrampolineLocation);
  },

  watch: {
    currentStep(newVal) {
      if (newVal && newVal.vowels) {
        let instructions = `Type the vowel${newVal.vowels.length > 1 ? 's' : ''} - ${
          newVal.vowels
        }`;

        setTimeout(() => {
          this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
        }, 2000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
%match {
  width: 135px;
  height: 50px;
}

.tablet .pulled {
  margin-left: -15px;
}

.grid {
  display: inline-grid;
  grid-template-columns: 32% 35% 32%;
  width: 1024px;

  #input {
    @extend %match;
    position: relative;
    text-align: center;
    font-weight: 500;
    font-size: 2.2em;
    margin: 0px auto;
  }

  figure {
    top: calc(100% - 124px);
    left: calc(1024px / 2);
    margin: 0px auto;
    position: absolute;
    transform: translateX(-50%);
    width: 255px;
    z-index: -1;
  }

  #left-side,
  #right-side {
    list-style-type: none;
    padding: 0;
    margin: 45px 25% 0px 25%;

    div.container {
      width: 285px;
      margin: -86% -35% auto;
      display: grid;
      grid-template-columns: 75px auto 75px;
      text-align: center;
      font-weight: 500;
      font-size: 2.2em;

      span,
      div {
        &.match {
          @extend %match;
        }

        &:nth-of-type(1) {
          position: relative;
          left: -15px;
        }

        &:nth-of-type(2) {
          position: relative;
          left: -15px;
        }

        &:nth-of-type(3) {
          position: relative;
          left: -15px;
        }
      }
    }

    div.isMobile {
      span,
      div {
        left: -15px !important;
        position: relative !important;
        text-align: center !important;
      }
    }
  }
}

#winner {
  text-align: center;

  #medal {
    margin: -2.5% auto;
    width: 80%;
  }

  figcaption {
    margin: -28% 0 0 -6%;
    font-size: 300%;
  }
}

.chrome-os {
  .grid {
    #left-side,
    #right-side {
      margin: 45px 30% 0px 30%;
    }
  }
}

@media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
  .grid {
    margin-left: 8%;
    width: 85%;

    figure {
      left: 50%;
    }

    #left-side,
    #right-side {
      margin: 0px 25%;
      min-width: 160px;

      div.container {
        margin: -88% -40% auto;
      }
    }
  }

  #winner {
    margin-left: 8%;
    width: 85%;
  }
}
</style>
